import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { getCarlitoHubAccessToken, useCarlitoHubAccessToken } from '../persistence';

import { carlitoApiQueryClient } from '@carlito/api-client';
import {
  getGuest,
  getHotelConfiguration,
  GetHotelConfigurationParams,
  getRoomBill,
  GuestLoginParams,
  listDoors,
  loginGuest,
  openDoor,
  updateGuest,
  UpdateGuestParams,
} from '../queries';
import { listGuests, ListGuestsParams } from '../queries/listGuests';
import { analyticIdentify } from '@carlito/app-analytics';

export const queryClient = carlitoApiQueryClient;

export * from './util';

export function onJwtChanged() {
  queryClient.refetchQueries();
}

export function useLoginGuest() {
  return useMutation((params: GuestLoginParams) => loginGuest(params), {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['guest'] });
    },
  });
}

export function useUpdateGuest() {
  return useMutation((params: UpdateGuestParams) => updateGuest(params), {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['guest'] });
    },
  });
}

export function useCurrentGuest() {
  const token = getCarlitoHubAccessToken();
  const enabled = Boolean(token);
  const { isLoading, data, ...rest } = useQuery(['guest'], () => getGuest({}), {
    enabled,
    retry: false,
    // 30 min
    refetchInterval: 1000 * 60 * 30,
    refetchOnWindowFocus: false,
  });
  const guest = data?.loggedInGuest?.guest;
  const isLoggedIn = Boolean(guest);
  const reservation = data?.loggedInGuest?.currentReservation;

  useEffect(() => {
    if (guest) analyticIdentify(guest.id);
  }, [guest]);

  return { isLoading: isLoading && enabled, reservation, isLoggedIn, data, ...rest, guest };
}

export function useGetHotelConfiguration(params?: GetHotelConfigurationParams) {
  const hotelConfig = useQuery(
    ['hotel', 'configuration', params],
    () => (params ? getHotelConfiguration(params) : undefined),
    {
      enabled: !!params && params.hotelId !== undefined,
    }
  );

  const supportedPmsFeatures = hotelConfig.data?.hotelConfiguration?.supportedPmsFeatures;
  const supportedPosFeatures = hotelConfig.data?.hotelConfiguration?.supportedPosFeatures;

  const hasPms =
    hotelConfig.data?.hotelConfiguration?.pms !== undefined &&
    hotelConfig.data?.hotelConfiguration?.pms !== 'none';

  const hasPos =
    hotelConfig.data?.hotelConfiguration?.pos !== undefined &&
    hotelConfig.data?.hotelConfiguration?.pos !== 'none' &&
    supportedPosFeatures?.orderBypassingAllowed;
  const hasSpaSystem =
    hotelConfig.data?.hotelConfiguration?.spaSystem !== undefined &&
    hotelConfig.data?.hotelConfiguration?.spaSystem !== 'none';

  return {
    ...hotelConfig,
    hasPms,
    hasPos,
    hasSpaSystem,
    supportedPmsFeatures,
    supportedPosFeatures,
  };
}

export function useClearCarlitoHubAccessTokenIfInvalid() {
  const { accessToken, clearCarlitoHubAccessToken } = useCarlitoHubAccessToken();
  const { data, isFetched, isFetching } = useCurrentGuest();

  const RELOAD_TIMEOUT = 7000;

  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null;

    // TODO: We only need this because for some reason the "enabled" flag doesn't work for useCurrentGuest.
    // Upgrade to react-query v5 and see if this is still needed.
    if (isFetched && !isFetching && (!data || !data.loggedInGuest?.guest) && accessToken) {
      timeoutId = setTimeout(() => {
        console.log({
          accessToken,
          data,
          isFetched,
          isFetching,
        });
        console.log('Reloading Carlito App because of invalid Hub token');
        clearCarlitoHubAccessToken();
        window.location.reload();
      }, RELOAD_TIMEOUT);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [accessToken, clearCarlitoHubAccessToken, data, isFetched, isFetching]);
}

type GetRoomBillOpts = {
  enabled?: boolean;
};

export function useGetRoomBill({ enabled = true }: GetRoomBillOpts = { enabled: true }) {
  return useQuery(['room', 'bill'], () => getRoomBill({}), {
    enabled,
  });
}

export function useListDoors() {
  return useQuery(['door', 'list'], () => listDoors(), {});
}

export function useOpenDoor() {
  return useMutation((doorId: string) => openDoor({ doorId }), {});
}

export function useListGuests(params: ListGuestsParams) {
  return useQuery(['guest', 'list', params], () => listGuests(params), {});
}

export { ListGuestsParams } from '../queries/listGuests';
